import { durationBetweenDates, formatAbsoluteDateTime, timeAgo } from "@/format/time";
import { usePrebuild } from "@/queries/prebuild-queries";
import { Timestamp } from "@bufbuild/protobuf";
import { Zap } from "lucide-react";
import { type FC, useState } from "react";
import { PrebuildLogsModal } from "@/components/projects/PrebuildLogsModal";
import { Tooltip } from "@/components/Tooltip";

type PrebuildBannerProps = {
    prebuildId?: string;
};

export const PrebuildBanner: FC<PrebuildBannerProps> = ({ prebuildId }) => {
    const { data: prebuild, error } = usePrebuild(prebuildId);
    const [showLogsModal, setShowLogsModal] = useState(false);

    // Don't render if no prebuild ID
    if (!prebuildId) {
        return null;
    }

    // Prebuild was deleted (404) - show minimal banner without logs link
    if (error) {
        return (
            <div className="px-6 pb-4">
                <div className="flex items-center justify-between rounded-lg border border-border-base bg-surface-secondary px-4 py-3">
                    <div className="flex items-center gap-3">
                        <Zap size={16} className="text-content-secondary" />
                        <span className="text-sm text-content-primary">Prebuild saved you time</span>
                    </div>
                </div>
            </div>
        );
    }

    // Loading state - don't show anything to avoid layout shift
    if (!prebuild) {
        return null;
    }

    const createdAt = prebuild.metadata?.createdAt ? new Timestamp(prebuild.metadata.createdAt).toDate() : undefined;
    const completedAt = prebuild.status?.completionTime
        ? new Timestamp(prebuild.status.completionTime).toDate()
        : undefined;

    const duration = createdAt && completedAt ? durationBetweenDates(createdAt, completedAt) : undefined;
    const relativeTime = createdAt ? timeAgo(createdAt) : undefined;
    const absoluteTime = createdAt ? formatAbsoluteDateTime(createdAt) : undefined;

    return (
        <>
            <div className="px-6 pb-4">
                <div className="flex items-center justify-between rounded-lg border border-border-base bg-surface-secondary px-4 py-3">
                    <div className="flex items-center gap-3">
                        <Zap size={16} className="text-content-secondary" />
                        <span className="text-sm text-content-primary">
                            <span>Prebuild saved you time</span>
                            {relativeTime && (
                                <>
                                    <span className="mx-1.5 text-content-tertiary">·</span>
                                    <Tooltip content={absoluteTime} usePortal>
                                        <span className="cursor-default">Ran {relativeTime}</span>
                                    </Tooltip>
                                </>
                            )}
                            {duration && <span className="text-content-secondary"> ({duration})</span>}
                        </span>
                    </div>
                    <button
                        onClick={() => setShowLogsModal(true)}
                        className="text-sm text-content-secondary hover:text-content-primary"
                        data-tracking-id="prebuild-banner-view-logs"
                    >
                        View logs
                    </button>
                </div>
            </div>
            {showLogsModal && (
                <PrebuildLogsModal prebuildId={prebuildId} open={showLogsModal} onOpenChange={setShowLogsModal} />
            )}
        </>
    );
};
